import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../../../CommonElements/Button";
import { FileField, SelectField, TextField } from "../../../CommonElements/FormFields";
import FieldError from "../../../CommonElements/FormFields/FieldError";
import PhoneField from "../../../CommonElements/PhoneField";
import { useLanguage } from "../../../context/LanguageContext";
import { useSignupModal } from "../../../context/SignupModalContext";
import { useToast } from "../../../context/ToastContext";
import { getLocalizedCountryOptions, resolveCountryCodeFromInput } from "../../../utils/countryOptions";
import { useCompetition } from "../../../context/CompetitionContext";
import {
  checkExistingContact,
  checkKyc,
  checkNicknameAvailability,
  getTermsPdfUrl,
  recoverAccount,
  registerParticipant,
  saveCompetitionNickname,
} from "../../../utils/api";
import { getPublicClientIp } from "../../../utils/clientIp";
import { DEFAULT_PHONE_COUNTRY_CODE, getPhoneCountryOption } from "../../../utils/phoneCountryOptions";
import {
  validateAccountNumber,
  validateRecoveryEmail,
  validateRecoveryPhone,
  validateNickname,
  validateSignupField,
  validateSignupFields,
  validateSignupTerms,
} from "../../../utils/signupValidation";
import {
  PROFESSION_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
} from "../../../Data/signupOptions";

const INITIAL_FORM = {
  fullName: "",
  email: "",
  countryOfResidence: "",
  phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
  phone: "",
  nationality: "",
  dualNationality: "na",
  profession: "",
  professionOther: "",
  sourceOfFunds: "",
  sourceOfFundsOther: "",
  proofOfIdentity: null,
  proofOfIdentityBack: null,
  proofOfAddress: null,
  usCitizen: "",
  beneficialOwner: false,
  termsAccepted: false,
  competitionTermsAccepted: false,
  hasReferralCode: false,
  referralCode: "",
  nickname: "",
};

const KYC_ADMIN_EMAIL = "cs@godocm.com";

const KycPendingMessage = ({ t }) => {
  const email = t("signup.kycPendingContactEmail") || KYC_ADMIN_EMAIL;
  const full = t("signup.kycPending");
  const parts = String(full).split(email);

  if (parts.length < 2) {
    return full;
  }

  return (
    <>
      {parts[0]}
      <a href={`mailto:${email}`}>{email}</a>
      {parts.slice(1).join(email)}
    </>
  );
};

const ExistingUserIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="8" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M5.5 19c.7-3.2 3.2-5 6.5-5s5.8 1.8 6.5 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const NewUserIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="10" cy="8" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M3.8 19c.7-3.1 3-4.9 6.2-4.9 1.3 0 2.5.3 3.5.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M17 13.5v7M13.5 17h7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const NicknameSuggestions = ({ t, suggestions = [], onPick }) => {
  if (!suggestions.length) return null;
  const shown = suggestions.slice(0, 2);
  return (
    <div className="signup-form__nickname-suggestions" role="group" aria-label={t("signup.nicknameSuggestions")}>
      {shown.map((item) => (
        <button
          key={item}
          type="button"
          className="signup-form__nickname-chip"
          onClick={() => onPick(item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
};

const ExistingNicknameFields = ({
  t,
  value,
  error,
  onChange,
}) => (
  <div className="signup-form__nickname">
    <TextField
      id="signup-existing-nickname"
      label={t("signup.nickname")}
      placeholder={t("signup.nicknamePlaceholder")}
      value={value}
      maxLength={16}
      error={error ? t(error) : ""}
      onChange={(event) => onChange(event.target.value)}
    />
    <p className="signup-form__lead">{t("signup.nicknameHint")}</p>
  </div>
);

const matchOptionValue = (options, raw, code) => {
  const candidates = [code, raw].map((value) => String(value || "").trim()).filter(Boolean);
  for (const candidate of candidates) {
    const byValue = options.find(
      (option) => option.value && option.value.toLowerCase() === candidate.toLowerCase()
    );
    if (byValue) return byValue.value;
    const byLabel = options.find(
      (option) => option.label && option.label.toLowerCase() === candidate.toLowerCase()
    );
    if (byLabel) return byLabel.value;
    const resolved = resolveCountryCodeFromInput(candidate);
    if (resolved && options.some((option) => option.value === resolved)) {
      return resolved;
    }
  }
  return "";
};

const ExistingProfileEditor = ({
  t,
  form,
  errors,
  countryOptions,
  nationalityOptions,
  dualNationalityOptions,
  professionOptions,
  fundsOptions,
  identityInfo,
  addressInfo,
  onChange,
  onCountryChange,
  onPhoneChange,
  onViewCompetitionTerms,
  nicknameStatus,
  onPickNickname,
  onNicknameBlur,
}) => (
  <div className="signup-form__edit-fields">
    <div className="signup-form__row">
      <TextField
        id="signup-edit-email"
        label={t("signup.email")}
        type="email"
        placeholder={t("signup.emailPlaceholder")}
        value={form.email}
        error={errors.email ? t(errors.email) : ""}
        onChange={onChange("email")}
      />
      <PhoneField
        id="signup-edit-phone"
        label={t("signup.phone")}
        placeholder="050 123 4567"
        countryCode={form.phoneCountryCode}
        phone={form.phone}
        error={errors.phone ? t(errors.phone) : ""}
        onCountryChange={onCountryChange}
        onPhoneChange={onPhoneChange}
      />
    </div>

    <div className="signup-form__row">
      <TextField
        id="signup-edit-fullName"
        label={t("signup.fullName")}
        placeholder={t("signup.fullNamePlaceholder")}
        value={form.fullName}
        error={errors.fullName ? t(errors.fullName) : ""}
        onChange={onChange("fullName")}
      />
      <div className="signup-form__nickname-block">
        <TextField
          id="signup-edit-nickname"
          label={t("signup.nickname")}
          placeholder={t("signup.nicknamePlaceholder")}
          value={form.nickname}
          maxLength={16}
          error={
            errors.nickname
              ? t(errors.nickname)
              : nicknameStatus?.errorKey
                ? t(nicknameStatus.errorKey)
                : ""
          }
          hint={
            errors.nickname || nicknameStatus?.errorKey
              ? ""
              : (nicknameStatus?.hintKey
                ? t(nicknameStatus.hintKey)
                : t("signup.nicknameHint"))
          }
          hintTone={nicknameStatus?.tone || ""}
          onChange={onChange("nickname")}
          onBlur={onNicknameBlur}
        />
        <NicknameSuggestions
          t={t}
          suggestions={nicknameStatus?.suggestions || []}
          onPick={onPickNickname}
        />
      </div>
      <SelectField
        id="signup-edit-dualNationality"
        label={t("signup.dualNationality")}
        value={form.dualNationality}
        onChange={onChange("dualNationality")}
        options={dualNationalityOptions}
      />
      <SelectField
        id="signup-edit-country"
        label={t("signup.countryOfResidence")}
        value={form.countryOfResidence}
        onChange={onChange("countryOfResidence")}
        options={countryOptions}
        error={errors.countryOfResidence ? t(errors.countryOfResidence) : ""}
      />
    </div>

    <div className="signup-form__row">
      <SelectField
        id="signup-edit-nationality"
        label={t("signup.nationality")}
        value={form.nationality}
        onChange={onChange("nationality")}
        options={nationalityOptions}
        error={errors.nationality ? t(errors.nationality) : ""}
      />
      <SelectField
        id="signup-edit-profession"
        label={t("signup.profession")}
        value={form.profession}
        onChange={onChange("profession")}
        options={professionOptions}
        otherValue={form.professionOther}
        onOtherChange={onChange("professionOther")}
        error={
          errors.profession
            ? t(errors.profession)
            : errors.professionOther
              ? t(errors.professionOther)
              : ""
        }
      />
      <SelectField
        id="signup-edit-funds"
        label={t("signup.sourceOfFunds")}
        value={form.sourceOfFunds}
        onChange={onChange("sourceOfFunds")}
        options={fundsOptions}
        otherValue={form.sourceOfFundsOther}
        onOtherChange={onChange("sourceOfFundsOther")}
        error={
          errors.sourceOfFunds
            ? t(errors.sourceOfFunds)
            : errors.sourceOfFundsOther
              ? t(errors.sourceOfFundsOther)
              : ""
        }
      />
    </div>

    <div className="signup-form__row">
      <FileField
        id="signup-edit-proofOfIdentity"
        label={t("signup.proofOfIdentity")}
        file={form.proofOfIdentity}
        error={errors.proofOfIdentity ? t(errors.proofOfIdentity) : ""}
        onChange={onChange("proofOfIdentity")}
        infoContent={identityInfo}
        infoLabel={t("signup.proofOfIdentityInfoLabel")}
        required={false}
      />
      <FileField
        id="signup-edit-proofOfIdentityBack"
        label={t("signup.proofOfIdentityBack")}
        file={form.proofOfIdentityBack}
        error={errors.proofOfIdentityBack ? t(errors.proofOfIdentityBack) : ""}
        onChange={onChange("proofOfIdentityBack")}
        required={false}
      />
      <FileField
        id="signup-edit-proofOfAddress"
        label={t("signup.proofOfAddress")}
        file={form.proofOfAddress}
        error={errors.proofOfAddress ? t(errors.proofOfAddress) : ""}
        onChange={onChange("proofOfAddress")}
        infoContent={addressInfo}
        infoLabel={t("signup.proofOfAddressInfoLabel")}
        required={false}
      />
    </div>
    <p className="signup-form__lead">{t("signup.editDocumentsHint")}</p>

    <div className="signup-form__radio-group" role="radiogroup" aria-labelledby="signup-edit-usCitizen-label">
      <span id="signup-edit-usCitizen-label" className="signup-form__label">
        {t("signup.usCitizen")}
        <span className="signup-form__required">*</span>
      </span>
      <div className="signup-form__radios">
        <label className="signup-form__radio">
          <input
            type="radio"
            name="signup-edit-usCitizen"
            checked={form.usCitizen === "yes"}
            onChange={() => onChange("usCitizen")({ target: { type: "radio", value: "yes" } })}
          />
          <span>{t("signup.yes")}</span>
        </label>
        <label className="signup-form__radio">
          <input
            type="radio"
            name="signup-edit-usCitizen"
            checked={form.usCitizen === "no"}
            onChange={() => onChange("usCitizen")({ target: { type: "radio", value: "no" } })}
          />
          <span>{t("signup.no")}</span>
        </label>
      </div>
      <FieldError id="signup-edit-usCitizen-error">
        {errors.usCitizen ? t(errors.usCitizen) : ""}
      </FieldError>
    </div>

    <TermsAgreements
      idPrefix="signup-edit"
      form={form}
      errors={errors}
      t={t}
      onChange={onChange}
      onViewCompetitionTerms={onViewCompetitionTerms}
    />
  </div>
);

const listedValueOrOther = (options, stored) => {
  const value = String(stored || "").trim();
  if (!value) return { value: "", other: "" };
  const known = options.some((option) => option.value && option.value === value);
  if (known) return { value, other: "" };
  return { value: "other", other: value };
};

const AccountDetails = ({ account, t, highlightAccount = false, onEdit }) => {
  if (!account) return null;

  const formatOption = (group, value) => {
    if (!value) return "—";
    const key = `signup.${group}.${value}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };

  const yesNo = (value) => {
    if (value == null) return "—";
    return value ? t("signup.yes") : t("signup.no");
  };

  const displayValue = (value) => {
    if (value == null || value === "") return "—";
    return value;
  };

  const sections = [
    {
      title: t("signup.detailsProfile"),
      rows: [
        ...(highlightAccount || !account.accountNumber
          ? []
          : [{ label: t("signup.accountNumber"), value: account.accountNumber }]),
        { label: t("signup.fullNameShort"), value: displayValue(account.fullName) },
        { label: t("signup.nickname"), value: displayValue(account.nickname) },
        { label: t("signup.emailShort"), value: displayValue(account.email) },
        { label: t("signup.phoneShort"), value: displayValue(account.phone) },
      ],
    },
    {
      title: t("signup.detailsLocation"),
      rows: [
        { label: t("signup.country"), value: displayValue(account.countryOfResidence) },
        { label: t("signup.nationality"), value: displayValue(account.nationality) },
        {
          label: t("signup.dualNationality"),
          value: account.dualNationality
            ? account.dualNationality
            : t("signup.notApplicable"),
        },
      ],
    },
    {
      title: t("signup.detailsCompliance"),
      rows: [
        {
          label: t("signup.profession"),
          value: formatOption("professionOptions", account.profession),
        },
        {
          label: t("signup.sourceOfFunds"),
          value: formatOption("fundsOptions", account.sourceOfFunds),
        },
        { label: t("signup.usCitizen"), value: yesNo(account.usCitizen) },
        {
          label: t("signup.beneficialOwnerShort"),
          value: yesNo(account.beneficialOwner),
        },
        {
          label: t("signup.termsAcceptedShort"),
          value: yesNo(account.termsAccepted),
        },
        {
          label: t("signup.kycStatus"),
          value: account.kycApproved ? t("signup.approved") : t("signup.notApproved"),
          tone: account.kycApproved ? "success" : "pending",
        },
      ],
    },
  ];

  return (
    <div className="signup-form__account-card">
      {onEdit ? (
        <div className="signup-form__edit-bar">
          <button type="button" className="signup-form__edit" onClick={onEdit}>
            {t("signup.editDetails")}
          </button>
        </div>
      ) : null}
      {highlightAccount && account.accountNumber ? (
        <div className="signup-form__account-hero">
          <span className="signup-form__account-hero-label">{t("signup.accountNumber")}</span>
          <strong className="signup-form__account-hero-value">{account.accountNumber}</strong>
        </div>
      ) : null}

      <div className="signup-form__detail-sections">
        {sections.map((section) => (
          <section key={section.title} className="signup-form__detail-section">
            <h3 className="signup-form__detail-heading">{section.title}</h3>
            <dl className="signup-form__account-details">
              {section.rows.map((row) => (
                <div key={row.label} className="signup-form__detail-item">
                  <dt>{row.label}</dt>
                  <dd className={row.tone ? `is-${row.tone}` : undefined}>
                    {row.tone ? (
                      <span className={`signup-form__status-pill is-${row.tone}`}>{row.value}</span>
                    ) : (
                      row.value
                    )}
                    {row.action}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
};

const isPreviewableImage = (file) => Boolean(file?.type && file.type.startsWith("image/"));

const TermsAgreements = ({
  idPrefix,
  form,
  errors,
  t,
  onChange,
  onViewCompetitionTerms,
}) => (
  <>
    <label className="signup-form__check" htmlFor={`${idPrefix}-beneficialOwner`}>
      <input
        id={`${idPrefix}-beneficialOwner`}
        type="checkbox"
        checked={form.beneficialOwner}
        onChange={onChange("beneficialOwner")}
      />
      <span>
        {t("signup.beneficialOwnerBefore")}
        <a
          href="https://godocm.com/fr/limited-power-of-attorney-account-manager"
          target="_blank"
          rel="noreferrer"
        >
          {t("signup.powerOfAttorney")}
        </a>
        {t("signup.beneficialOwnerAfter")}
      </span>
    </label>
    <FieldError id={`${idPrefix}-beneficialOwner-error`}>
      {errors.beneficialOwner ? t(errors.beneficialOwner) : ""}
    </FieldError>

    <label className="signup-form__check" htmlFor={`${idPrefix}-termsAccepted`}>
      <input
        id={`${idPrefix}-termsAccepted`}
        type="checkbox"
        checked={form.termsAccepted}
        onChange={onChange("termsAccepted")}
      />
      <span>
        {t("signup.termsBefore")}
        <a
          href="https://godocm.com/wp-content/uploads/2025/01/Terms-of-Business.pdf"
          target="_blank"
          rel="noreferrer"
        >
          {t("signup.termsOfBusiness")}
        </a>
        {t("signup.termsAfter")}
      </span>
    </label>
    <FieldError id={`${idPrefix}-termsAccepted-error`}>
      {errors.termsAccepted ? t(errors.termsAccepted) : ""}
    </FieldError>

    <label className="signup-form__check" htmlFor={`${idPrefix}-competitionTermsAccepted`}>
      <input
        id={`${idPrefix}-competitionTermsAccepted`}
        type="checkbox"
        checked={form.competitionTermsAccepted}
        onChange={onChange("competitionTermsAccepted")}
      />
      <span>
        {t("signup.competitionTermsBefore")}
        <button
          type="button"
          className="signup-form__terms-link"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onViewCompetitionTerms();
          }}
        >
          {t("signup.competitionTermsLink")}
        </button>
        {t("signup.competitionTermsAfter")}
      </span>
    </label>
    <FieldError id={`${idPrefix}-competitionTermsAccepted-error`}>
      {errors.competitionTermsAccepted ? t(errors.competitionTermsAccepted) : ""}
    </FieldError>
  </>
);

const FilePreviewCard = ({ label, file, url, viewLabel, onView }) => {
  if (!file) return null;
  const image = isPreviewableImage(file);

  return (
    <div className="signup-form__preview-card">
      <p className="signup-form__preview-label">{label}</p>
      <button
        type="button"
        className="signup-form__preview-thumb"
        onClick={() => onView({ label, file, url })}
      >
        {image && url ? (
          <img src={url} alt={file.name} />
        ) : (
          <span className="signup-form__preview-fallback">{file.name}</span>
        )}
      </button>
      <p className="signup-form__preview-name">{file.name}</p>
      <button type="button" className="signup-form__preview-link" onClick={() => onView({ label, file, url })}>
        {viewLabel}
      </button>
    </div>
  );
};

const SignupModal = () => {
  const { open, closeSignup } = useSignupModal();
  const { showToast } = useToast();
  const { t, locale } = useLanguage();
  const { competition, competitionId } = useCompetition();
  const [form, setForm] = useState(INITIAL_FORM);
  const [competitionTermsViewed, setCompetitionTermsViewed] = useState(false);
  const [userType, setUserType] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [accountError, setAccountError] = useState("");
  const [kycStatus, setKycStatus] = useState(null);
  const [kycMessage, setKycMessage] = useState("");
  const [kycAccount, setKycAccount] = useState(null);
  const [errors, setErrors] = useState({});
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoverMethod, setRecoverMethod] = useState("email");
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverPhone, setRecoverPhone] = useState("");
  const [recoverPhoneCountry, setRecoverPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY_CODE);
  const [recoverError, setRecoverError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingKyc, setIsCheckingKyc] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [registeredAccount, setRegisteredAccount] = useState(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);
  const [existingNickname, setExistingNickname] = useState("");
  const [nicknameError, setNicknameError] = useState("");
  const [nicknameSaved, setNicknameSaved] = useState("");
  const [isSavingNickname, setIsSavingNickname] = useState(false);
  const [editingNickname, setEditingNickname] = useState(false);
  const [profileForm, setProfileForm] = useState(null);
  const [profileErrors, setProfileErrors] = useState({});
  const [nicknameStatus, setNicknameStatus] = useState(null);
  const [profileNicknameStatus, setProfileNicknameStatus] = useState(null);
  const [nicknameDirty, setNicknameDirty] = useState(false);
  const [profileNicknameDirty, setProfileNicknameDirty] = useState(false);
  const nicknameCheckRef = useRef(0);
  const [previewViewer, setPreviewViewer] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});
  const formRef = useRef(form);
  formRef.current = form;


  const countryOptions = useMemo(() => {
    const countries = getLocalizedCountryOptions(locale);
    return [
      { value: "", label: t("signup.select"), searchLabel: "select" },
      ...countries,
    ];
  }, [locale, t]);

  const nationalityOptions = useMemo(
    () => [
      { value: "", label: t("signup.selectShort"), searchLabel: "select" },
      ...countryOptions.filter((option) => option.value),
    ],
    [countryOptions, t]
  );

  const dualNationalityOptions = useMemo(
    () => [
      {
        value: "na",
        label: t("signup.notApplicable"),
        searchLabel: "not applicable na",
      },
      ...nationalityOptions,
    ],
    [nationalityOptions, t]
  );

  const professionOptions = useMemo(
    () =>
      PROFESSION_OPTIONS.map((option) =>
        option.value
          ? {
              ...option,
              label: t(`signup.professionOptions.${option.value}`),
              searchLabel: `${t(`signup.professionOptions.${option.value}`)} ${option.label}`,
            }
          : { ...option, label: t("signup.select") }
      ),
    [t]
  );

  const fundsOptions = useMemo(
    () =>
      SOURCE_OF_FUNDS_OPTIONS.map((option) =>
        option.value
          ? {
              ...option,
              label: t(`signup.fundsOptions.${option.value}`),
              searchLabel: `${t(`signup.fundsOptions.${option.value}`)} ${option.label}`,
            }
          : { ...option, label: t("signup.selectShort") }
      ),
    [t]
  );

  const startProfileEdit = (account) => {
    if (!account) return;
    const profession = listedValueOrOther(PROFESSION_OPTIONS, account.profession);
    const funds = listedValueOrOther(SOURCE_OF_FUNDS_OPTIONS, account.sourceOfFunds);
    const phoneCountry = getPhoneCountryOption(DEFAULT_PHONE_COUNTRY_CODE);
    setProfileForm({
      fullName: account.fullName || "",
      nickname: account.nickname || "",
      email: account.email || "",
      phone: account.phone || "",
      phoneCountryCode: phoneCountry?.code || DEFAULT_PHONE_COUNTRY_CODE,
      countryOfResidence: matchOptionValue(
        countryOptions,
        account.countryOfResidence,
        account.residencyCode
      ),
      nationality: matchOptionValue(countryOptions, account.nationality, account.nationalityCode),
      dualNationality: account.dualNationality
        ? (matchOptionValue(countryOptions, account.dualNationality) || "na")
        : "na",
      profession: profession.value,
      professionOther: profession.other,
      sourceOfFunds: funds.value,
      sourceOfFundsOther: funds.other,
      proofOfIdentity: null,
      proofOfIdentityBack: null,
      proofOfAddress: null,
      usCitizen: account.usCitizen == null ? "" : account.usCitizen ? "yes" : "no",
      beneficialOwner: Boolean(account.beneficialOwner),
      termsAccepted: Boolean(account.termsAccepted),
      competitionTermsAccepted: Boolean(account.competitionTermsAccepted),
    });
    setProfileErrors({});
    setExistingNickname(account.nickname || "");
    setProfileNicknameStatus(null);
    setProfileNicknameDirty(false);
    setEditingNickname(true);
  };

  const mapNicknameCheckResult = (result) => {
    if (!result) return null;
    if (result.available) {
      return {
        tone: "success",
        hintKey: "signup.nicknameAvailable",
        errorKey: "",
        suggestions: [],
        available: true,
      };
    }
    if (result.reason === "taken") {
      return {
        tone: "error",
        hintKey: "",
        errorKey: "signup.errors.nicknameTaken",
        suggestions: result.suggestions || [],
        available: false,
      };
    }
    if (result.reason === "reserved") {
      return {
        tone: "error",
        hintKey: "",
        errorKey: "signup.errors.nicknameReserved",
        suggestions: result.suggestions || [],
        available: false,
      };
    }
    if (result.reason === "length") {
      return {
        tone: "error",
        hintKey: "",
        errorKey: "signup.errors.nicknameLength",
        suggestions: [],
        available: false,
      };
    }
    if (result.reason === "invalid") {
      return {
        tone: "error",
        hintKey: "",
        errorKey: "signup.errors.nicknameInvalid",
        suggestions: [],
        available: false,
      };
    }
    return null;
  };

  const runNicknameAvailabilityCheck = async ({
    nickname,
    accountNumber: accountForCheck,
    email,
    setStatus,
  }) => {
    const formatError = validateNickname(nickname, { required: false });
    if (!String(nickname || "").trim()) {
      setStatus(null);
      return;
    }
    if (formatError) {
      setStatus({
        tone: "error",
        errorKey: formatError,
        hintKey: "",
        suggestions: [],
        available: false,
      });
      return;
    }

    const requestId = ++nicknameCheckRef.current;
    setStatus({
      tone: "checking",
      hintKey: "signup.nicknameChecking",
      errorKey: "",
      suggestions: [],
      available: null,
    });

    try {
      const { ok, payload } = await checkNicknameAvailability({
        nickname,
        accountNumber: accountForCheck,
        email,
      });
      if (requestId !== nicknameCheckRef.current) return;
      if (!ok) {
        setStatus({
          tone: "error",
          errorKey: payload?.message || "toast.submitError",
          hintKey: "",
          suggestions: [],
          available: false,
        });
        return;
      }
      setStatus(mapNicknameCheckResult(payload?.result));
    } catch {
      if (requestId !== nicknameCheckRef.current) return;
      setStatus(null);
    }
  };

  const setProfileField = (key) => (event) => {
    const target = event?.target;
    const nextValue = !target
      ? event
      : target.type === "checkbox"
        ? target.checked
        : target.type === "file"
          ? target.files?.[0] || null
          : target.value;
    if (key === "nickname") setProfileNicknameDirty(true);
    setProfileForm((current) => (current ? { ...current, [key]: nextValue } : current));
    setProfileErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const checkProfileNicknameNow = () => {
    if (!profileForm) return;
    setProfileNicknameDirty(true);
    void runNicknameAvailabilityCheck({
      nickname: profileForm.nickname,
      accountNumber: kycAccount?.accountNumber || accountNumber,
      email: profileForm.email,
      setStatus: setProfileNicknameStatus,
    });
  };

  useEffect(() => {
    if (userType !== "new" || registeredAccount) {
      setNicknameStatus(null);
      setNicknameDirty(false);
      return undefined;
    }
    // Keep last availability result while reviewing; don't clear or re-check.
    if (reviewMode) return undefined;
    if (!nicknameDirty) {
      setNicknameStatus(null);
      return undefined;
    }

    const value = form.nickname;
    const timer = window.setTimeout(() => {
      void runNicknameAvailabilityCheck({
        nickname: value,
        email: form.email,
        setStatus: setNicknameStatus,
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [form.nickname, form.email, userType, reviewMode, registeredAccount, nicknameDirty]);

  useEffect(() => {
    if (!profileForm) {
      setProfileNicknameStatus(null);
      setProfileNicknameDirty(false);
      return undefined;
    }
    if (!profileNicknameDirty) {
      setProfileNicknameStatus(null);
      return undefined;
    }
    const value = profileForm.nickname;
    const timer = window.setTimeout(() => {
      void runNicknameAvailabilityCheck({
        nickname: value,
        accountNumber: kycAccount?.accountNumber || accountNumber,
        email: profileForm.email,
        setStatus: setProfileNicknameStatus,
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [
    profileForm?.nickname,
    profileForm?.email,
    profileForm,
    profileNicknameDirty,
    kycAccount?.accountNumber,
    accountNumber,
  ]);

  const identityInfo = useMemo(
    () => ({
      intro: t("signup.proofOfIdentityIntro"),
      items: t("signup.proofOfIdentityItems"),
    }),
    [t]
  );

  const addressInfo = useMemo(
    () => ({
      intro: t("signup.proofOfAddressIntro"),
      items: t("signup.proofOfAddressItems"),
    }),
    [t]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (previewViewer) {
        setPreviewViewer(null);
        return;
      }
      closeSignup();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, closeSignup, previewViewer]);

  useEffect(() => {
    if (open) getPublicClientIp();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (competition?.countryEligible === false) {
      showToast(t("signup.errors.countryNotEligible"), "error");
      closeSignup();
    }
  }, [open, competition?.countryEligible, closeSignup, showToast, t]);

  useEffect(() => {
    if (!open) {
      setForm(INITIAL_FORM);
      setUserType(null);
      setAccountNumber("");
      setAccountError("");
      setKycStatus(null);
      setKycMessage("");
      setKycAccount(null);
      setErrors({});
      setRecoveryMode(false);
      setRecoverMethod("email");
      setRecoverEmail("");
      setRecoverPhone("");
      setRecoverPhoneCountry(DEFAULT_PHONE_COUNTRY_CODE);
      setRecoverError("");
      setReviewMode(false);
      setRegisteredAccount(null);
      setIsCheckingExisting(false);
      setPreviewViewer(null);
      setIsCheckingKyc(false);
      setCompetitionTermsViewed(false);
      setProfileForm(null);
      setProfileErrors({});
      setEditingNickname(false);
      setExistingNickname("");
      setNicknameError("");
      setNicknameSaved("");
      setIsSavingNickname(false);
      setNicknameStatus(null);
      setProfileNicknameStatus(null);
      setNicknameDirty(false);
      setProfileNicknameDirty(false);
    }
  }, [open]);

  useEffect(() => {
    const next = {};
    ["proofOfIdentity", "proofOfIdentityBack", "proofOfAddress"].forEach((key) => {
      if (form[key] instanceof File) {
        next[key] = URL.createObjectURL(form[key]);
      }
    });
    setPreviewUrls(next);

    return () => {
      Object.values(next).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [form.proofOfIdentity, form.proofOfIdentityBack, form.proofOfAddress]);

  const clearFieldError = (...keys) => {
    setErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((key) => {
        if (next[key]) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const setField = (key) => (event) => {
    const { type, checked, value, files } = event.target;
    const nextValue =
      type === "checkbox" ? checked : type === "file" ? files?.[0] || null : value;

    if (key === "nickname") setNicknameDirty(true);

    setForm((prev) => {
      const next = {
        ...prev,
        [key]: nextValue,
      };

      if (key === "hasReferralCode" && !checked) {
        next.referralCode = "";
      }

      if (key === "profession" && value !== "other") {
        next.professionOther = "";
      }

      if (key === "sourceOfFunds" && value !== "other") {
        next.sourceOfFundsOther = "";
      }

      return next;
    });

    if (key === "profession") clearFieldError("profession", "professionOther");
    else if (key === "sourceOfFunds") clearFieldError("sourceOfFunds", "sourceOfFundsOther");
    else if (key === "hasReferralCode") clearFieldError("referralCode");
    else clearFieldError(key);
  };

  const validateFieldOnBlur = (key) => () => {
    setForm((current) => {
      const message = validateSignupField(key, current);
      setErrors((prev) => {
        const next = { ...prev };
        if (message) next[key] = message;
        else delete next[key];
        return next;
      });
      return current;
    });
  };

  const resetToUserTypeChoice = () => {
    setUserType(null);
    setForm(INITIAL_FORM);
    setAccountNumber("");
    setAccountError("");
    setKycStatus(null);
    setKycMessage("");
    setKycAccount(null);
    setErrors({});
    setRecoveryMode(false);
    setRecoverMethod("email");
    setRecoverEmail("");
    setRecoverPhone("");
    setRecoverPhoneCountry(DEFAULT_PHONE_COUNTRY_CODE);
    setRecoverError("");
    setReviewMode(false);
    setRegisteredAccount(null);
    setIsCheckingExisting(false);
    setIsSubmitting(false);
    setPreviewViewer(null);
    setCompetitionTermsViewed(false);
    setProfileForm(null);
    setProfileErrors({});
    setEditingNickname(false);
    setExistingNickname("");
    setNicknameError("");
    setNicknameSaved("");
    setIsSavingNickname(false);
    setNicknameStatus(null);
    setProfileNicknameStatus(null);
    setNicknameDirty(false);
    setProfileNicknameDirty(false);
  };

  const notifyExistingAndReturnToChoice = (messageKey) => {
    showToast(t(messageKey || "toast.alreadyExists"), "warning");
    resetToUserTypeChoice();
  };

  const blockIfCountryIneligible = (payload) => {
    if (payload?.message !== "signup.errors.countryNotEligible") return false;
    showToast(t("signup.errors.countryNotEligible"), "error");
    closeSignup();
    return true;
  };

  const checkExistingOnBlur = async () => {
    const current = formRef.current;
    const emailError = validateSignupField("email", current);
    const phoneError = validateSignupField("phone", current);
    if (emailError || phoneError) return;

    setIsCheckingExisting(true);
    try {
      const { ok, payload } = await checkExistingContact({
        email: current.email,
        phone: current.phone,
        phoneCountryCode: current.phoneCountryCode,
        fullName: current.fullName,
        countryOfResidence: current.countryOfResidence,
        nationality: current.nationality,
        dualNationality: current.dualNationality,
        profession: current.profession,
        professionOther: current.professionOther,
        sourceOfFunds: current.sourceOfFunds,
        sourceOfFundsOther: current.sourceOfFundsOther,
        usCitizen: current.usCitizen,
        hasReferralCode: current.hasReferralCode,
        referralCode: current.referralCode,
      });
      if (blockIfCountryIneligible(payload)) return;
      const result = payload?.result;
      if (!ok || !result?.exists) return;

      notifyExistingAndReturnToChoice(payload?.message || result.message || "toast.alreadyExists");
    } catch {
      // Ignore lookup failures; registration can still proceed.
    } finally {
      setIsCheckingExisting(false);
    }
  };

  const openCompetitionTerms = () => {
    const rawId = competitionId || competition?.id || null;
    const id = rawId && String(rawId) !== "static-demo" ? rawId : undefined;
    const pdfUrl = getTermsPdfUrl(id, locale);

    if (!pdfUrl) {
      showToast(t("terms.unavailable"), "warning");
      return;
    }

    window.open(pdfUrl, "_blank", "noopener,noreferrer");
    setCompetitionTermsViewed(true);
  };

  const setRadio = (key, value) => () => {
    setForm((prev) => ({ ...prev, [key]: value }));
    clearFieldError(key);
  };

  const setPhoneCountry = (code) => {
    setForm((prev) => ({ ...prev, phoneCountryCode: code }));
  };

  const goBack = () => {
    if (registeredAccount) {
      closeSignup();
      return;
    }

    if (reviewMode) {
      setReviewMode(false);
      return;
    }

    if (recoveryMode) {
      setRecoveryMode(false);
      setRecoverError("");
      setKycStatus(null);
      setKycMessage("");
      setKycAccount(null);
      return;
    }

    resetToUserTypeChoice();
  };

  const openRecovery = () => {
    setRecoveryMode(true);
    setAccountError("");
    setKycStatus(null);
    setKycMessage("");
    setKycAccount(null);
    setRecoverError("");
    setProfileForm(null);
    setProfileErrors({});
    setEditingNickname(false);
  };

  const mapLookupAccount = (account) => {
    if (!account) return null;
    const fullName = account.fullName ||
      [account.firstName, account.middleName, account.lastName].filter(Boolean).join(" ").trim();
    return {
      accountNumber: account.accountNumber || (account.id != null ? String(account.id) : ""),
      fullName,
      email: account.email || "",
      phone: account.phone || "",
      countryOfResidence: account.countryOfResidence || account.country || account.residency || "",
      residencyCode: account.residencyCode || account.countryCode || account.nation || "",
      nationality: account.nationality || "",
      nationalityCode: account.nationalityCode || "",
      dualNationality: account.dualNationality || account.otherCountryNational || "",
      profession: account.profession || "",
      sourceOfFunds: account.sourceOfFunds || "",
      usCitizen:
        account.usCitizen == null
          ? null
          : account.usCitizen === true || account.usCitizen === "yes" || account.usCitizen === 1,
      referralCode: account.referralCode || account.referrar || "",
      nickname: account.nickname || account.competitionNickname || "",
      beneficialOwner: Boolean(account.beneficialOwner ?? account.acceptedClientAgreement),
      termsAccepted: Boolean(account.termsAccepted ?? account.acceptedTerms),
      competitionTermsAccepted: Boolean(
        account.competitionTermsAccepted ?? account.acceptedCompetitionTerms
      ),
      kycApproved: Boolean(account.kycApproved) || account.kycStatus === "approved",
    };
  };

  const parseLookupPayload = (payload) => {
    const result = payload?.result;
    if (result && typeof result === "object" && !Array.isArray(result) && result.status) {
      return {
        status: result.status,
        account: mapLookupAccount(result.account),
        error: result.error || payload?.message || "",
      };
    }

    return {
      status: "error",
      account: null,
      error: "toast.kycCheckError",
    };
  };

  const handleExistingSubmit = async (event) => {
    event.preventDefault();
    if (profileForm) return;
    setKycStatus(null);
    setKycMessage("");
    setKycAccount(null);

    const validationError = validateAccountNumber(accountNumber);
    if (validationError) {
      setAccountError(validationError);
      return;
    }

    setAccountError("");
    setIsCheckingKyc(true);

    try {
      const { payload } = await checkKyc(accountNumber.trim());
      if (blockIfCountryIneligible(payload)) return;
      const result = parseLookupPayload(payload);

      setKycStatus(result.status);
      setKycMessage(result.error || "");
      setKycAccount(result.account);
      setExistingNickname(result.account?.nickname || "");
      setEditingNickname(false);
      setProfileForm(null);
      setProfileErrors({});
      setNicknameError("");
      setNicknameSaved("");
    } catch {
      setKycStatus("error");
      setKycMessage("toast.kycCheckError");
    } finally {
      setIsCheckingKyc(false);
    }
  };

  const handleRecoverSubmit = async (event) => {
    event.preventDefault();
    if (profileForm) return;
    setKycStatus(null);
    setKycMessage("");
    setKycAccount(null);

    if (recoverMethod === "email") {
      const emailError = validateRecoveryEmail(recoverEmail);
      if (emailError) {
        setRecoverError(emailError);
        return;
      }
    } else {
      const phoneError = validateRecoveryPhone(recoverPhone);
      if (phoneError) {
        setRecoverError(phoneError);
        return;
      }
    }

    setRecoverError("");
    setIsCheckingKyc(true);

    try {
      const { payload } = await recoverAccount({
        method: recoverMethod,
        email: recoverEmail,
        phone: recoverPhone,
        phoneCountryCode: recoverPhoneCountry,
      });
      if (blockIfCountryIneligible(payload)) return;
      const result = parseLookupPayload(payload);

      setKycStatus(result.status);
      setKycMessage(result.error || "");
      setKycAccount(result.account);
      setExistingNickname(result.account?.nickname || "");
      setEditingNickname(false);
      setProfileForm(null);
      setProfileErrors({});
      setNicknameError("");
      setNicknameSaved("");

      if (result.account?.accountNumber) {
        setAccountNumber(result.account.accountNumber);
      }
    } catch {
      setKycStatus("error");
      setKycMessage("toast.kycCheckError");
    } finally {
      setIsCheckingKyc(false);
    }
  };

  const handleSaveExistingNickname = async () => {
    if (profileForm) {
      const errors = {
        ...validateSignupFields(profileForm, [
          "fullName",
          "nickname",
          "email",
          "phone",
          "countryOfResidence",
          "nationality",
          "profession",
          "professionOther",
          "sourceOfFunds",
          "sourceOfFundsOther",
          "usCitizen",
        ]),
        ...validateSignupTerms(profileForm),
      };
      if (profileNicknameDirty && profileNicknameStatus?.available === false) {
        errors.nickname = profileNicknameStatus.errorKey || "signup.errors.nicknameTaken";
      }
      if (
        profileNicknameDirty
        && profileNicknameStatus?.available == null
        && String(profileForm.nickname || "").trim()
      ) {
        errors.nickname = "signup.nicknameChecking";
      }
      if (Object.keys(errors).length) {
        setProfileErrors(errors);
        showToast(t(Object.values(errors)[0]), "error");
        return;
      }
    } else {
      const validationError = validateNickname(existingNickname, { required: true });
      if (validationError) {
        setNicknameError(validationError);
        setNicknameSaved("");
        showToast(t(validationError), "error");
        return;
      }
    }

    setNicknameError("");
    setNicknameSaved("");
    setIsSavingNickname(true);
    try {
      const payloadBody = profileForm
        ? {
            accountNumber: kycAccount?.accountNumber || accountNumber,
            method: recoverMethod,
            ...profileForm,
            nickname: profileForm.nickname.trim(),
          }
        : {
            accountNumber: kycAccount?.accountNumber || accountNumber,
            method: recoverMethod,
            email: recoverEmail,
            phone: recoverPhone,
            phoneCountryCode: recoverPhoneCountry,
            nickname: existingNickname.trim(),
          };
      const { ok, payload } = await saveCompetitionNickname(payloadBody);
      if (!ok) {
        const message = payload?.message || "toast.submitError";
        if (profileForm) {
          setProfileErrors({ form: message });
        } else {
          setNicknameError(message);
        }
        showToast(t(message), "error");
        return;
      }
      const savedAccount = mapLookupAccount(payload?.result?.account) || kycAccount;
      const savedFromEdit = Boolean(profileForm);
      const toastKey = payload?.message
        || (savedFromEdit ? "signup.detailsSaved" : "signup.nicknameSaved");
      setExistingNickname(savedAccount?.nickname || existingNickname.trim());
      setKycAccount(savedAccount);
      setProfileForm(null);
      setEditingNickname(false);
      showToast(t(toastKey), "success");
    } catch {
      const message = "toast.submitError";
      setNicknameError(message);
      showToast(t(message), "error");
    } finally {
      setIsSavingNickname(false);
    }
  };

  const registerErrorMessage = (payloadOrError) => {
    const message =
      typeof payloadOrError === "string"
        ? payloadOrError
        : payloadOrError?.message || payloadOrError?.payload?.message || "";
    const raw = String(message || "").trim();
    const lower = raw.toLowerCase();

    if (!raw) return t("toast.submitError");
    if (raw.startsWith("signup.") || raw.startsWith("toast.")) return t(raw);

    if (
      lower.includes("jpeg")
      || lower.includes("png")
      || lower.includes("webp")
      || (lower.includes("only") && lower.includes("pdf"))
      || lower.includes("file type")
      || lower.includes("not allowed")
      || lower.includes("docx")
      || lower.includes("unsupported")
    ) {
      return t("signup.errors.fileType");
    }
    if (lower.includes("file too large") || lower.includes("limit_file_size")) {
      return t("signup.errors.fileTooLarge");
    }
    if (lower.includes("already registered as client")) return t("toast.alreadyClient");
    if (lower.includes("already exists")) return t("toast.alreadyExists");
    if (lower.includes("proof of identity") || lower.includes("proof of address")
      || lower.includes("documents are required")) {
      return t("toast.documentsRequired");
    }
    if (lower.includes("too many")) return t("toast.rateLimited");
    if (lower.includes("validation")) return t("toast.reviewFields");

    return t("toast.submitError");
  };

  const optionLabel = (options, value) =>
    options.find((option) => option.value === value)?.label || value || "—";

  const reviewValues = useMemo(() => {
    const phoneOption = getPhoneCountryOption(form.phoneCountryCode);
    const phone = form.phone
      ? `+${phoneOption?.dialCode || ""} ${form.phone}`.trim()
      : "—";
    const profession =
      form.profession === "other"
        ? form.professionOther
        : optionLabel(professionOptions, form.profession);
    const funds =
      form.sourceOfFunds === "other"
        ? form.sourceOfFundsOther
        : optionLabel(fundsOptions, form.sourceOfFunds);
    const dual =
      !form.dualNationality || form.dualNationality === "na"
        ? t("signup.notApplicable")
        : optionLabel(nationalityOptions, form.dualNationality);

    return {
      fullName: form.fullName || "—",
      nickname: form.nickname || "—",
      email: form.email || "—",
      phone,
      countryOfResidence: optionLabel(countryOptions, form.countryOfResidence),
      nationality: optionLabel(nationalityOptions, form.nationality),
      dualNationality: dual,
      profession: profession || "—",
      sourceOfFunds: funds || "—",
      usCitizen:
        form.usCitizen === "yes"
          ? t("signup.yes")
          : form.usCitizen === "no"
            ? t("signup.no")
            : "—",
      referralCode: form.hasReferralCode ? form.referralCode || "—" : "",
    };
  }, [
    countryOptions,
    form,
    fundsOptions,
    nationalityOptions,
    professionOptions,
    t,
  ]);

  const handleReview = async (event) => {
    event.preventDefault();

    const nextErrors = {
      ...validateSignupFields(form),
      ...validateSignupTerms(form),
    };
    if (nicknameDirty && nicknameStatus?.available === false) {
      nextErrors.nickname = nicknameStatus.errorKey || "signup.errors.nicknameTaken";
    }
    if (
      nicknameDirty
      && nicknameStatus?.available == null
      && String(form.nickname || "").trim()
    ) {
      nextErrors.nickname = "signup.nicknameChecking";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      showToast(t(nextErrors.nickname || "toast.reviewFields"), "error");
      return;
    }

    setIsCheckingExisting(true);
    try {
      const { ok, payload } = await checkExistingContact({
        email: form.email,
        phone: form.phone,
        phoneCountryCode: form.phoneCountryCode,
        fullName: form.fullName,
        countryOfResidence: form.countryOfResidence,
        nationality: form.nationality,
        dualNationality: form.dualNationality,
        profession: form.profession,
        professionOther: form.professionOther,
        sourceOfFunds: form.sourceOfFunds,
        sourceOfFundsOther: form.sourceOfFundsOther,
        usCitizen: form.usCitizen,
        hasReferralCode: form.hasReferralCode,
        referralCode: form.referralCode,
      });
      if (ok && payload?.result?.exists) {
        notifyExistingAndReturnToChoice(payload?.message || payload?.result?.message || "toast.alreadyExists");
        return;
      }
    } catch {
      // Continue to review if the lookup fails.
    } finally {
      setIsCheckingExisting(false);
    }

    setErrors({});
    setReviewMode(true);
  };

  const buildAccountFromRegistration = (account) => {
    const phoneOption = getPhoneCountryOption(form.phoneCountryCode);
    const phone =
      account?.phone ||
      (form.phone ? `+${phoneOption?.dialCode || ""} ${form.phone}`.trim() : "");
    const dual =
      !form.dualNationality || form.dualNationality === "na"
        ? ""
        : optionLabel(nationalityOptions, form.dualNationality);

    return {
      accountNumber: account?.accountNumber || "",
      fullName: account?.fullName || form.fullName,
      nickname: account?.nickname || form.nickname,
      email: account?.email || form.email,
      phone,
      countryOfResidence:
        account?.countryOfResidence ||
        optionLabel(countryOptions, form.countryOfResidence),
      nationality:
        account?.nationality || optionLabel(nationalityOptions, form.nationality),
      dualNationality: account?.dualNationality || dual,
      profession:
        account?.profession ||
        (form.profession === "other"
          ? form.professionOther
          : form.profession),
      sourceOfFunds:
        account?.sourceOfFunds ||
        (form.sourceOfFunds === "other"
          ? form.sourceOfFundsOther
          : form.sourceOfFunds),
      usCitizen:
        account?.usCitizen != null
          ? account.usCitizen
          : form.usCitizen === "yes"
            ? true
            : form.usCitizen === "no"
              ? false
              : null,
      referralCode:
        account?.referralCode ||
        (form.hasReferralCode ? form.referralCode : ""),
      beneficialOwner: Boolean(form.beneficialOwner || account?.beneficialOwner),
      termsAccepted: Boolean(form.termsAccepted || account?.termsAccepted),
      kycApproved:
        Boolean(account?.kycApproved) || account?.kycStatus === "approved",
    };
  };

  const handleRegister = async () => {
    const termErrors = validateSignupTerms(form);
    if (Object.keys(termErrors).length) {
      setErrors(termErrors);
      showToast(t("toast.reviewFields"), "error");
      return;
    }
    if (nicknameDirty && nicknameStatus?.available === false) {
      setErrors((current) => ({
        ...current,
        nickname: nicknameStatus.errorKey || "signup.errors.nicknameTaken",
      }));
      showToast(t(nicknameStatus.errorKey || "signup.errors.nicknameTaken"), "error");
      setReviewMode(false);
      return;
    }
    if (
      nicknameDirty
      && nicknameStatus?.available == null
      && String(form.nickname || "").trim()
    ) {
      showToast(t("signup.nicknameChecking"), "error");
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const { ok, payload } = await registerParticipant(form);
      if (!ok || payload?.isSuccess === false) {
        if (blockIfCountryIneligible(payload)) return;
        showToast(registerErrorMessage(payload), "error");
        return;
      }

      const alreadyExists = Boolean(payload?.result?.alreadyExists);
      if (alreadyExists) {
        notifyExistingAndReturnToChoice(payload?.message || "toast.alreadyExists");
        return;
      }

      const account = payload?.result?.account;
      if (!account?.accountNumber) {
        showToast(t("toast.submitError"), "error");
        return;
      }

      setRegisteredAccount(buildAccountFromRegistration(account));
      setReviewMode(false);
      showToast(t("toast.registerSuccess"), "success", {
        title: t("signup.registerSuccessTitle"),
      });
    } catch (err) {
      showToast(registerErrorMessage(err?.message || err), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const title =
    userType === "existing"
      ? recoveryMode
        ? t("signup.forgotAccountTitle")
        : t("signup.existingUser")
      : registeredAccount
        ? t("signup.registerSuccessTitle")
        : userType === "new" && reviewMode
          ? t("signup.reviewTitle")
          : userType === "new"
            ? t("signup.newUser")
            : t("signup.title");

  return createPortal(
    <div
      className="signup-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={closeSignup}
    >
      <div
        className={`signup-modal__dialog${userType ? "" : " signup-modal__dialog--choice"}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="signup-modal__header">
          {userType ? (
            <button
              type="button"
              className="signup-modal__back"
              aria-label={t("signup.back")}
              onClick={goBack}
            />
          ) : null}
          <h2 className="signup-modal__title">{title}</h2>
          <button
            type="button"
            className="signup-modal__close"
            aria-label={t("signup.close")}
            onClick={closeSignup}
          />
        </div>

        {!userType ? (
          <div className="signup-form">
            <p className="signup-form__lead">{t("signup.lead")}</p>
            <div className="signup-form__type-grid">
              <button
                type="button"
                className="signup-form__type-card signup-form__type-card--existing"
                onClick={() => setUserType("existing")}
              >
                <span className="signup-form__type-top">
                  <span className="signup-form__type-icon">
                    <ExistingUserIcon />
                  </span>
                  <span className="signup-form__type-body">
                    <strong>{t("signup.existingUser")}</strong>
                    <span className="signup-form__type-hint">{t("signup.existingHint")}</span>
                  </span>
                </span>
                <span className="signup-form__type-action">{t("signup.checkKyc")}</span>
              </button>
              <button
                type="button"
                className="signup-form__type-card signup-form__type-card--new"
                onClick={() => setUserType("new")}
              >
                <span className="signup-form__type-top">
                  <span className="signup-form__type-icon">
                    <NewUserIcon />
                  </span>
                  <span className="signup-form__type-body">
                    <strong>{t("signup.newUser")}</strong>
                    <span className="signup-form__type-hint">{t("signup.newHint")}</span>
                  </span>
                </span>
                <span className="signup-form__type-action">{t("signup.registerNow")}</span>
              </button>
            </div>
          </div>
        ) : null}

        {userType === "existing" && !recoveryMode ? (
          <form className="signup-form" noValidate onSubmit={handleExistingSubmit}>
            <TextField
              id="signup-accountNumber"
              label={t("signup.accountNumber")}
              placeholder={t("signup.accountPlaceholder")}
              value={accountNumber}
              error={accountError ? t(accountError) : ""}
              onChange={(event) => {
                setAccountNumber(event.target.value);
                setAccountError("");
                setKycStatus(null);
                setKycMessage("");
                setKycAccount(null);
                setProfileForm(null);
                setEditingNickname(false);
              }}
            />

            {!profileForm ? (
              <button type="button" className="signup-form__forgot" onClick={openRecovery}>
                {t("signup.forgotAccount")}
              </button>
            ) : null}

            {kycAccount && (kycStatus === "approved" || kycStatus === "pending") ? (
              <div className="signup-form__account" role="status">
                {!profileForm ? (
                  <p
                    className={`signup-form__status ${
                      kycStatus === "approved"
                        ? "signup-form__status--success"
                        : "signup-form__status--pending"
                    }`}
                  >
                    {kycStatus === "approved" ? t("signup.kycApproved") : <KycPendingMessage t={t} />}
                  </p>
                ) : null}
                {profileForm ? (
                  <ExistingProfileEditor
                    t={t}
                    form={profileForm}
                    errors={profileErrors}
                    countryOptions={countryOptions}
                    nationalityOptions={nationalityOptions}
                    dualNationalityOptions={dualNationalityOptions}
                    professionOptions={professionOptions}
                    fundsOptions={fundsOptions}
                    identityInfo={identityInfo}
                    addressInfo={addressInfo}
                    onChange={setProfileField}
                    onCountryChange={(value) => setProfileField("phoneCountryCode")({ target: { value } })}
                    onPhoneChange={setProfileField("phone")}
                    onViewCompetitionTerms={openCompetitionTerms}
                    nicknameStatus={profileNicknameDirty ? profileNicknameStatus : null}
                    onNicknameBlur={checkProfileNicknameNow}
                    onPickNickname={(value) => {
                      setProfileNicknameDirty(true);
                      setProfileField("nickname")({ target: { value } });
                      setProfileErrors((current) => {
                        if (!current.nickname) return current;
                        const next = { ...current };
                        delete next.nickname;
                        return next;
                      });
                    }}
                  />
                ) : (
                  <AccountDetails
                    account={kycAccount}
                    t={t}
                    onEdit={() => startProfileEdit(kycAccount)}
                  />
                )}
              </div>
            ) : null}

            {kycStatus === "not_found" || kycStatus === "error" ? (
              <p className="signup-form__status signup-form__status--error" role="alert">
                {kycStatus === "not_found" ? t("signup.kycNotFound") : t("toast.kycCheckError")}
              </p>
            ) : null}

            {profileForm && kycAccount ? (
              <div className="signup-form__actions">
                <Button
                  type="button"
                  variant="solid"
                  size="md"
                  className="signup-form__submit signup-form__submit--ghost"
                  onClick={() => {
                    setProfileForm(null);
                    setEditingNickname(false);
                    setProfileErrors({});
                    setProfileNicknameStatus(null);
                    setProfileNicknameDirty(false);
                  }}
                  disabled={isSavingNickname}
                >
                  {t("signup.cancel")}
                </Button>
                <Button
                  type="button"
                  variant="solid"
                  size="md"
                  className="signup-form__submit"
                  onClick={handleSaveExistingNickname}
                  disabled={
                    isSavingNickname
                    || (profileNicknameDirty
                      && (profileNicknameStatus?.available === false
                        || (profileNicknameStatus?.available == null
                          && Boolean(String(profileForm?.nickname || "").trim()))))
                  }
                >
                  {isSavingNickname ? t("signup.savingNickname") : t("signup.saveNickname")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="solid"
                size="md"
                className="signup-form__submit"
                disabled={isCheckingKyc}
              >
                {isCheckingKyc ? t("signup.checkingKyc") : t("signup.checkKyc")}
              </Button>
            )}
          </form>
        ) : null}

        {userType === "existing" && recoveryMode ? (
          <form className="signup-form" noValidate onSubmit={handleRecoverSubmit}>
            <p className="signup-form__lead">{t("signup.recoverLead")}</p>

            <div className="signup-form__radio-group" role="radiogroup" aria-label={t("signup.forgotAccountTitle")}>
              <div className="signup-form__radios">
                <label className="signup-form__radio">
                  <input
                    type="radio"
                    name="signup-recover-method"
                    checked={recoverMethod === "email"}
                    onChange={() => {
                      setRecoverMethod("email");
                      setRecoverError("");
                      setKycStatus(null);
                      setKycMessage("");
                      setKycAccount(null);
                    }}
                  />
                  <span>{t("signup.recoverByEmail")}</span>
                </label>
                <label className="signup-form__radio">
                  <input
                    type="radio"
                    name="signup-recover-method"
                    checked={recoverMethod === "phone"}
                    onChange={() => {
                      setRecoverMethod("phone");
                      setRecoverError("");
                      setKycStatus(null);
                      setKycMessage("");
                      setKycAccount(null);
                    }}
                  />
                  <span>{t("signup.recoverByPhone")}</span>
                </label>
              </div>
            </div>

            {recoverMethod === "email" ? (
              <TextField
                id="signup-recoverEmail"
                label={t("signup.email")}
                type="email"
                placeholder={t("signup.emailPlaceholder")}
                value={recoverEmail}
                error={recoverError && recoverMethod === "email" ? t(recoverError) : ""}
                onChange={(event) => {
                  setRecoverEmail(event.target.value);
                  setRecoverError("");
                  setKycStatus(null);
                  setKycMessage("");
                  setKycAccount(null);
                }}
              />
            ) : (
              <PhoneField
                id="signup-recoverPhone"
                label={t("signup.phone")}
                placeholder="050 123 4567"
                countryCode={recoverPhoneCountry}
                phone={recoverPhone}
                error={recoverError && recoverMethod === "phone" ? t(recoverError) : ""}
                onCountryChange={setRecoverPhoneCountry}
                onPhoneChange={(event) => {
                  setRecoverPhone(event.target.value);
                  setRecoverError("");
                  setKycStatus(null);
                  setKycMessage("");
                  setKycAccount(null);
                }}
              />
            )}

            {kycAccount && kycStatus !== "not_found" && kycStatus !== "error" ? (
              <div className="signup-form__account" role="status">
                {!profileForm ? (
                  <p
                    className={`signup-form__status ${
                      kycStatus === "approved"
                        ? "signup-form__status--success"
                        : "signup-form__status--pending"
                    }`}
                  >
                    {kycStatus === "approved" ? t("signup.kycApproved") : <KycPendingMessage t={t} />}
                  </p>
                ) : null}
                {profileForm ? (
                  <ExistingProfileEditor
                    t={t}
                    form={profileForm}
                    errors={profileErrors}
                    countryOptions={countryOptions}
                    nationalityOptions={nationalityOptions}
                    dualNationalityOptions={dualNationalityOptions}
                    professionOptions={professionOptions}
                    fundsOptions={fundsOptions}
                    identityInfo={identityInfo}
                    addressInfo={addressInfo}
                    onChange={setProfileField}
                    onCountryChange={(value) => setProfileField("phoneCountryCode")({ target: { value } })}
                    onPhoneChange={setProfileField("phone")}
                    onViewCompetitionTerms={openCompetitionTerms}
                    nicknameStatus={profileNicknameDirty ? profileNicknameStatus : null}
                    onNicknameBlur={checkProfileNicknameNow}
                    onPickNickname={(value) => {
                      setProfileNicknameDirty(true);
                      setProfileField("nickname")({ target: { value } });
                      setProfileErrors((current) => {
                        if (!current.nickname) return current;
                        const next = { ...current };
                        delete next.nickname;
                        return next;
                      });
                    }}
                  />
                ) : (
                  <AccountDetails
                    account={kycAccount}
                    t={t}
                    onEdit={() => startProfileEdit(kycAccount)}
                  />
                )}
              </div>
            ) : null}

            {kycStatus === "not_found" || kycStatus === "error" ? (
              <p className="signup-form__status signup-form__status--error" role="alert">
                {kycStatus === "not_found" ? t("signup.recoverNotFound") : t("toast.kycCheckError")}
              </p>
            ) : null}

            {profileForm && kycAccount ? (
              <div className="signup-form__actions">
                <Button
                  type="button"
                  variant="solid"
                  size="md"
                  className="signup-form__submit signup-form__submit--ghost"
                  onClick={() => {
                    setProfileForm(null);
                    setEditingNickname(false);
                    setProfileErrors({});
                    setProfileNicknameStatus(null);
                    setProfileNicknameDirty(false);
                  }}
                  disabled={isSavingNickname}
                >
                  {t("signup.cancel")}
                </Button>
                <Button
                  type="button"
                  variant="solid"
                  size="md"
                  className="signup-form__submit"
                  onClick={handleSaveExistingNickname}
                  disabled={
                    isSavingNickname
                    || (profileNicknameDirty
                      && (profileNicknameStatus?.available === false
                        || (profileNicknameStatus?.available == null
                          && Boolean(String(profileForm?.nickname || "").trim()))))
                  }
                >
                  {isSavingNickname ? t("signup.savingNickname") : t("signup.saveNickname")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="solid"
                size="md"
                className="signup-form__submit"
                disabled={isCheckingKyc}
              >
                {isCheckingKyc ? t("signup.checkingKyc") : t("signup.findAccount")}
              </Button>
            )}
          </form>
        ) : null}

        {userType === "new" && registeredAccount ? (
          <div className="signup-form">
            <div className="signup-form__account" role="status">
              <p className="signup-form__status signup-form__status--success">
                {t("signup.registerSuccessLead")}
              </p>
              <AccountDetails account={registeredAccount} t={t} highlightAccount />
            </div>
            <Button
              type="button"
              variant="solid"
              size="md"
              className="signup-form__submit"
              onClick={closeSignup}
            >
              {t("signup.registerDone")}
            </Button>
          </div>
        ) : null}

        {userType === "new" && reviewMode && !registeredAccount ? (
          <div className="signup-form">
            <p className="signup-form__lead">{t("signup.reviewLead")}</p>

            <div className="signup-form__row">
              <TextField
                id="signup-review-email"
                label={t("signup.email")}
                value={reviewValues.email}
                readOnly
                required={false}
              />
              <TextField
                id="signup-review-phone"
                label={t("signup.phone")}
                value={reviewValues.phone}
                readOnly
                required={false}
              />
            </div>

            <div className="signup-form__row">
              <TextField
                id="signup-review-fullName"
                label={t("signup.fullName")}
                value={reviewValues.fullName}
                readOnly
                required={false}
              />
              <TextField
                id="signup-review-nickname"
                label={t("signup.nickname")}
                value={reviewValues.nickname}
                readOnly
                required={false}
              />
              <TextField
                id="signup-review-dualNationality"
                label={t("signup.dualNationality")}
                value={reviewValues.dualNationality}
                readOnly
                required={false}
              />
            </div>

            <div className="signup-form__row">
              <TextField
                id="signup-review-countryOfResidence"
                label={t("signup.countryOfResidence")}
                value={reviewValues.countryOfResidence}
                readOnly
                required={false}
              />
              <TextField
                id="signup-review-nationality"
                label={t("signup.nationality")}
                value={reviewValues.nationality}
                readOnly
                required={false}
              />
            </div>

            <div className="signup-form__row">
              <TextField
                id="signup-review-profession"
                label={t("signup.profession")}
                value={reviewValues.profession}
                readOnly
                required={false}
              />
              <TextField
                id="signup-review-sourceOfFunds"
                label={t("signup.sourceOfFunds")}
                value={reviewValues.sourceOfFunds}
                readOnly
                required={false}
              />
            </div>

            <p className="signup-form__section-title">{t("signup.reviewDocuments")}</p>
            <div className="signup-form__preview-grid">
              <FilePreviewCard
                label={t("signup.proofOfIdentity")}
                file={form.proofOfIdentity}
                url={previewUrls.proofOfIdentity}
                viewLabel={t("signup.viewFile")}
                onView={setPreviewViewer}
              />
              <FilePreviewCard
                label={t("signup.proofOfIdentityBack")}
                file={form.proofOfIdentityBack}
                url={previewUrls.proofOfIdentityBack}
                viewLabel={t("signup.viewFile")}
                onView={setPreviewViewer}
              />
              <FilePreviewCard
                label={t("signup.proofOfAddress")}
                file={form.proofOfAddress}
                url={previewUrls.proofOfAddress}
                viewLabel={t("signup.viewFile")}
                onView={setPreviewViewer}
              />
            </div>

            <div className="signup-form__row">
              <TextField
                id="signup-review-usCitizen"
                label={t("signup.usCitizen")}
                value={reviewValues.usCitizen}
                readOnly
                required={false}
              />
            </div>

            <TermsAgreements
              idPrefix="signup-review"
              form={form}
              errors={errors}
              t={t}
              onChange={setField}
              competitionTermsViewed={competitionTermsViewed}
              onViewCompetitionTerms={openCompetitionTerms}
            />

            <div className="signup-form__actions">
              <Button
                type="button"
                variant="solid"
                size="md"
                className="signup-form__submit signup-form__submit--ghost"
                onClick={() => setReviewMode(false)}
                disabled={isSubmitting}
              >
                {t("signup.editDetails")}
              </Button>
              <Button
                type="button"
                variant="solid"
                size="md"
                className="signup-form__submit"
                onClick={handleRegister}
                disabled={isSubmitting}
              >
                {isSubmitting ? t("signup.submitting") : t("signup.confirmRegister")}
              </Button>
            </div>
            {previewViewer ? (
              <div
                className="signup-form__lightbox"
                role="dialog"
                aria-modal="true"
                aria-label={previewViewer.label}
                onClick={() => setPreviewViewer(null)}
              >
                <div className="signup-form__lightbox-dialog" onClick={(event) => event.stopPropagation()}>
                  <div className="signup-form__lightbox-header">
                    <strong>{previewViewer.label}</strong>
                    <button
                      type="button"
                      className="signup-modal__close"
                      aria-label={t("signup.close")}
                      onClick={() => setPreviewViewer(null)}
                    />
                  </div>
                  {isPreviewableImage(previewViewer.file) ? (
                    <img src={previewViewer.url} alt={previewViewer.file.name} />
                  ) : (
                    <iframe title={previewViewer.file.name} src={previewViewer.url} />
                  )}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {userType === "new" && !reviewMode && !registeredAccount ? (
          <form className="signup-form" noValidate onSubmit={handleReview}>
            {isCheckingExisting ? (
              <div className="signup-form__existing-banner" role="status">
                {t("signup.checkingExisting")}
              </div>
            ) : null}

            <div className="signup-form__row">
              <TextField
                id="signup-email"
                label={t("signup.email")}
                type="email"
                placeholder={t("signup.emailPlaceholder")}
                value={form.email}
                error={errors.email ? t(errors.email) : ""}
                onChange={setField("email")}
                onBlur={() => {
                  validateFieldOnBlur("email")();
                  checkExistingOnBlur();
                }}
              />
              <PhoneField
                id="signup-phone"
                label={t("signup.phone")}
                placeholder="050 123 4567"
                countryCode={form.phoneCountryCode}
                phone={form.phone}
                error={errors.phone ? t(errors.phone) : ""}
                onCountryChange={setPhoneCountry}
                onPhoneChange={setField("phone")}
                onBlur={() => {
                  validateFieldOnBlur("phone")();
                  checkExistingOnBlur();
                }}
              />
            </div>

            <div className="signup-form__row">
              <TextField
                id="signup-fullName"
                label={t("signup.fullName")}
                placeholder={t("signup.fullNamePlaceholder")}
                value={form.fullName}
                error={errors.fullName ? t(errors.fullName) : ""}
                onChange={setField("fullName")}
                onBlur={validateFieldOnBlur("fullName")}
              />
              <div className="signup-form__nickname-block">
                <TextField
                  id="signup-nickname"
                  label={t("signup.nickname")}
                  placeholder={t("signup.nicknamePlaceholder")}
                  value={form.nickname}
                  maxLength={16}
                  error={
                    errors.nickname
                      ? t(errors.nickname)
                      : nicknameDirty && nicknameStatus?.errorKey
                        ? t(nicknameStatus.errorKey)
                        : ""
                  }
                  hint={
                    errors.nickname || (nicknameDirty && nicknameStatus?.errorKey)
                      ? ""
                      : (nicknameDirty && nicknameStatus?.hintKey
                        ? t(nicknameStatus.hintKey)
                        : t("signup.nicknameHint"))
                  }
                  hintTone={nicknameDirty ? (nicknameStatus?.tone || "") : ""}
                  onChange={setField("nickname")}
                  onBlur={() => {
                    if (String(form.nickname || "").trim()) {
                      setNicknameDirty(true);
                      void runNicknameAvailabilityCheck({
                        nickname: form.nickname,
                        email: form.email,
                        setStatus: setNicknameStatus,
                      });
                    }
                    validateFieldOnBlur("nickname")();
                  }}
                />
                <NicknameSuggestions
                  t={t}
                  suggestions={nicknameDirty ? (nicknameStatus?.suggestions || []) : []}
                  onPick={(value) => {
                    setNicknameDirty(true);
                    setField("nickname")({ target: { value } });
                    setErrors((current) => {
                      if (!current.nickname) return current;
                      const next = { ...current };
                      delete next.nickname;
                      return next;
                    });
                  }}
                />
              </div>
              <SelectField
                id="signup-dualNationality"
                label={t("signup.dualNationality")}
                value={form.dualNationality}
                onChange={setField("dualNationality")}
                options={dualNationalityOptions}
              />
               <SelectField
                id="signup-countryOfResidence"
                label={t("signup.countryOfResidence")}
                value={form.countryOfResidence}
                onChange={setField("countryOfResidence")}
                options={countryOptions}
                error={errors.countryOfResidence ? t(errors.countryOfResidence) : ""}
              />
            </div>

            <div className="signup-form__row">
             
              <SelectField
                id="signup-nationality"
                label={t("signup.nationality")}
                value={form.nationality}
                onChange={setField("nationality")}
                options={nationalityOptions}
                error={errors.nationality ? t(errors.nationality) : ""}
              />
       
              <SelectField
                id="signup-profession"
                label={t("signup.profession")}
                value={form.profession}
                onChange={setField("profession")}
                options={professionOptions}
                otherValue={form.professionOther}
                onOtherChange={setField("professionOther")}
                error={
                  errors.profession
                    ? t(errors.profession)
                    : errors.professionOther
                      ? t(errors.professionOther)
                      : ""
                }
              />
              <SelectField
                id="signup-sourceOfFunds"
                label={t("signup.sourceOfFunds")}
                value={form.sourceOfFunds}
                onChange={setField("sourceOfFunds")}
                options={fundsOptions}
                otherValue={form.sourceOfFundsOther}
                onOtherChange={setField("sourceOfFundsOther")}
                error={
                  errors.sourceOfFunds
                    ? t(errors.sourceOfFunds)
                    : errors.sourceOfFundsOther
                      ? t(errors.sourceOfFundsOther)
                      : ""
                }
              />
            </div>

            <div className="signup-form__row">
              <FileField
                id="signup-proofOfIdentity"
                label={t("signup.proofOfIdentity")}
                file={form.proofOfIdentity}
                error={errors.proofOfIdentity ? t(errors.proofOfIdentity) : ""}
                onChange={(event) => {
                  setField("proofOfIdentity")(event);
                  const file = event.target.files?.[0] || null;
                  setErrors((prev) => {
                    const next = { ...prev };
                    if (file) delete next.proofOfIdentity;
                    else next.proofOfIdentity = "signup.errors.identityRequired";
                    return next;
                  });
                }}
                infoContent={identityInfo}
                infoLabel={t("signup.proofOfIdentityInfoLabel")}
              />
              <FileField
                id="signup-proofOfIdentityBack"
                label={t("signup.proofOfIdentityBack")}
                file={form.proofOfIdentityBack}
                error={errors.proofOfIdentityBack ? t(errors.proofOfIdentityBack) : ""}
                onChange={(event) => {
                  setField("proofOfIdentityBack")(event);
                  const file = event.target.files?.[0] || null;
                  setErrors((prev) => {
                    const next = { ...prev };
                    if (file) delete next.proofOfIdentityBack;
                    else next.proofOfIdentityBack = "signup.errors.identityBackRequired";
                    return next;
                  });
                }}
              />
            </div>

            <div className="signup-form__row">
              <FileField
                id="signup-proofOfAddress"
                label={t("signup.proofOfAddress")}
                file={form.proofOfAddress}
                error={errors.proofOfAddress ? t(errors.proofOfAddress) : ""}
                onChange={(event) => {
                  setField("proofOfAddress")(event);
                  const file = event.target.files?.[0] || null;
                  setErrors((prev) => {
                    const next = { ...prev };
                    if (file) delete next.proofOfAddress;
                    else next.proofOfAddress = "signup.errors.addressRequired";
                    return next;
                  });
                }}
                infoContent={addressInfo}
                infoLabel={t("signup.proofOfAddressInfoLabel")}
              />
            </div>

            <div className="signup-form__radio-group" role="radiogroup" aria-labelledby="signup-usCitizen-label">
              <span id="signup-usCitizen-label" className="signup-form__label">
                {t("signup.usCitizen")}
                <span className="signup-form__required">*</span>
              </span>
              <div className="signup-form__radios">
                <label className="signup-form__radio">
                  <input
                    type="radio"
                    name="signup-usCitizen"
                    checked={form.usCitizen === "yes"}
                    onChange={setRadio("usCitizen", "yes")}
                  />
                  <span>{t("signup.yes")}</span>
                </label>
                <label className="signup-form__radio">
                  <input
                    type="radio"
                    name="signup-usCitizen"
                    checked={form.usCitizen === "no"}
                    onChange={setRadio("usCitizen", "no")}
                  />
                  <span>{t("signup.no")}</span>
                </label>
              </div>
              <FieldError id="signup-usCitizen-error">
                {errors.usCitizen ? t(errors.usCitizen) : ""}
              </FieldError>
            </div>

            <TermsAgreements
              idPrefix="signup"
              form={form}
              errors={errors}
              t={t}
              onChange={setField}
              competitionTermsViewed={competitionTermsViewed}
              onViewCompetitionTerms={openCompetitionTerms}
            />

            <Button
              type="submit"
              variant="solid"
              size="md"
              className="signup-form__submit"
              disabled={
                isCheckingExisting
                || (nicknameDirty
                  && (nicknameStatus?.available === false
                    || (nicknameStatus?.available == null
                      && Boolean(String(form.nickname || "").trim()))))
              }
            >
              {t("signup.reviewAndContinue")}
            </Button>
          </form>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default SignupModal;
